import React, { useState, useMemo, useRef, useCallback } from 'react';
import { Badge } from './ui/badge';
import { ScrollArea } from './ui/scroll-area';
import {
  ChevronDown, X, ArrowLeftRight, ArrowRight, Eye, EyeOff,
  List, LayoutGrid, Cable, Trash2, ArrowRightLeft, Info, MoreHorizontal,
  Grid3x3, Clock, DollarSign, Table2, Columns3, Activity,
} from 'lucide-react';
import { Button } from './ui/button';
import { SimpleGridView, TimelineView, SpendTreemap, IntegrationMatrix, SwimlanesView, HealthPulseView } from './MobileStackViews';
import { vendorLogoUrl } from '../lib/logoUtils';

export default function CardVisualization({ stackData, selectedProperty, onSelectNode, onClose, hideUnconnected: initialHide }) {
  const [expandedCats, setExpandedCats] = useState({});
  const [selectedId, setSelectedId] = useState(null);
  const [hideUnconnected, setHideUnconnected] = useState(initialHide || false);
  const [viewMode, setViewMode] = useState('cards'); // 'cards', 'compact', 'integrations'
  const [activeCatTab, setActiveCatTab] = useState(null);
  const [longPressNode, setLongPressNode] = useState(null); // {node, x, y}
  const [swipedCard, setSwipedCard] = useState(null);
  const [hideWeb, setHideWeb] = useState(false); // {nodeId, direction}
  const longPressTimer = useRef(null);
  const touchStart = useRef(null);
  const catTabsRef = useRef(null);

  const nodes = stackData?.nodes || [];
  const links = stackData?.links || [];
  const productLinks = stackData?.product_links || [];

  // Build connection map — include product names from nodes
  const connectionMap = useMemo(() => {
    const map = {};
    links.forEach(l => {
      const s = typeof l.source === 'object' ? l.source.id : l.source;
      const t = typeof l.target === 'object' ? l.target.id : l.target;
      const sNode = nodes.find(n => n.id === s);
      const tNode = nodes.find(n => n.id === t);
      if (!map[s]) map[s] = [];
      if (!map[t]) map[t] = [];
      map[s].push({ id: t, name: tNode?.short || tNode?.name || t, productName: tNode?.product_name || tNode?.vendor_name || '', direction: l.direction, level: 'platform', label: l.label, status: l.integration_status });
      map[t].push({ id: s, name: sNode?.short || sNode?.name || s, productName: sNode?.product_name || sNode?.vendor_name || '', direction: l.direction, level: 'platform', label: l.label, status: l.integration_status });
    });
    productLinks.forEach(l => {
      const sNode = nodes.find(n => n.id === l.source);
      const tNode = nodes.find(n => n.id === l.target);
      if (!map[l.source]) map[l.source] = [];
      if (!map[l.target]) map[l.target] = [];
      const entry = { direction: l.direction, level: 'product', label: l.label || '', status: l.integration_status, confirmed: l.property_confirmed };
      map[l.source].push({ ...entry, id: l.target, name: tNode?.short || tNode?.name || l.target, productName: l.to_product_name || tNode?.product_name || '' });
      map[l.target].push({ ...entry, id: l.source, name: sNode?.short || sNode?.name || l.source, productName: l.from_product_name || sNode?.product_name || '' });
    });
    return map;
  }, [links, productLinks, nodes]);

  const getConnections = useCallback((nodeId) => connectionMap[nodeId] || [], [connectionMap]);

  // Categories
  const categories = useMemo(() => {
    const groups = {};
    nodes.forEach(n => {
      if (hideUnconnected && !n.used) return;
      const cat = n.category_name || 'Other';
      if (!groups[cat]) groups[cat] = { color: n.category_color || '#6366f1', nodes: [] };
      groups[cat].nodes.push(n);
    });
    return Object.entries(groups).filter(([, c]) => c.nodes.length > 0).sort((a, b) => {
      const aU = a[1].nodes.filter(n => n.used).length;
      const bU = b[1].nodes.filter(n => n.used).length;
      if (bU !== aU) return bU - aU;
      return a[0].localeCompare(b[0]);
    });
  }, [nodes, hideUnconnected]);

  // All integration pairs for the flow list
  const integrationPairs = useMemo(() => {
    const pairs = [];
    const seen = new Set();
    links.forEach(l => {
      const s = typeof l.source === 'object' ? l.source.id : l.source;
      const t = typeof l.target === 'object' ? l.target.id : l.target;
      const sN = nodes.find(n => n.id === s);
      const tN = nodes.find(n => n.id === t);
      if (!sN?.used || !tN?.used) return;
      const key = [s, t].sort().join('-');
      if (seen.has(key)) return;
      seen.add(key);
      pairs.push({ source: sN, target: tN, direction: l.direction, label: l.label, status: l.integration_status, level: 'platform' });
    });
    productLinks.forEach(l => {
      const sN = nodes.find(n => n.id === l.source);
      const tN = nodes.find(n => n.id === l.target);
      if (!sN?.used || !tN?.used) return;
      const key = [l.source, l.target].sort().join('-p-');
      if (seen.has(key)) return;
      seen.add(key);
      pairs.push({ source: sN, target: tN, direction: l.direction, label: l.label, status: l.integration_status, level: 'product', fromProduct: l.from_product_name, toProduct: l.to_product_name, confirmed: l.property_confirmed });
    });
    return pairs;
  }, [links, productLinks, nodes]);

  const handleSelect = (node) => {
    setSelectedId(selectedId === node.id ? null : node.id);
    setSwipedCard(null);
    setLongPressNode(null);
    if (onSelectNode) onSelectNode(node);
  };

  // Status dot color
  const getStatusDot = (node) => {
    if (!node.used) return 'bg-gray-300';
    const conns = getConnections(node.id);
    const hasGaps = conns.some(c => c.status === 'gap');
    if (hasGaps) return 'bg-amber-400';
    if (conns.length > 0) return 'bg-emerald-400';
    return 'bg-emerald-400';
  };

  // Long press handlers
  const handleTouchStart = (e, node) => {
    touchStart.current = { x: e.touches[0].clientX, y: e.touches[0].clientY, nodeId: node.id };
    longPressTimer.current = setTimeout(() => {
      setLongPressNode({ node, x: e.touches[0].clientX, y: e.touches[0].clientY });
    }, 500);
  };

  const handleTouchMove = (e) => {
    if (!touchStart.current) return;
    const dx = e.touches[0].clientX - touchStart.current.x;
    const dy = e.touches[0].clientY - touchStart.current.y;
    // Cancel long press if moved
    if (Math.abs(dx) > 10 || Math.abs(dy) > 10) {
      clearTimeout(longPressTimer.current);
      // Detect horizontal swipe
      if (Math.abs(dx) > 50 && Math.abs(dy) < 30) {
        setSwipedCard({ nodeId: touchStart.current.nodeId, direction: dx > 0 ? 'right' : 'left' });
      }
    }
  };

  const handleTouchEnd = () => {
    clearTimeout(longPressTimer.current);
    touchStart.current = null;
  };

  const lighten = (hex, amt = 40) => {
    try {
      let r = parseInt(hex.slice(1, 3), 16), g = parseInt(hex.slice(3, 5), 16), b = parseInt(hex.slice(5, 7), 16);
      r = Math.min(255, r + amt); g = Math.min(255, g + amt); b = Math.min(255, b + amt);
      return `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}`;
    } catch { return '#f1f5f9'; }
  };

  const isPropertyView = selectedProperty && selectedProperty !== 'overall';
  const visibleCount = categories.reduce((s, [, c]) => s + c.nodes.length, 0);
  const activeCount = categories.reduce((s, [, c]) => s + c.nodes.filter(n => n.used).length, 0);

  // Mini connection web data (for radial mini-view)
  const webNodes = useMemo(() => {
    return nodes.filter(n => n.used).slice(0, 20);
  }, [nodes]);

  return (
    <div className="flex flex-col h-full pb-mobile-nav" data-testid="card-visualization">
      {/* Header */}
      <div className="shrink-0 px-3 py-2 border-b bg-card/50 flex items-center justify-between gap-1.5">
        <div>
          <h3 className="text-sm font-bold leading-tight">Stack Views</h3>
          <p className="text-[10px] text-muted-foreground">{activeCount} active / {visibleCount} platforms</p>
        </div>
        <div className="flex items-center gap-1">
          {isPropertyView && (
            <Button variant="ghost" size="sm" className="h-7 text-[10px] gap-1 px-2" onClick={() => setHideUnconnected(v => !v)} data-testid="card-hide-unconnected">
              {hideUnconnected ? <EyeOff className="h-3 w-3" /> : <Eye className="h-3 w-3" />}
              {hideUnconnected ? 'All' : 'Active'}
            </Button>
          )}
          {onClose && <button onClick={onClose} className="p-1 rounded hover:bg-muted/60"><X className="h-3.5 w-3.5" /></button>}
        </div>
      </div>

      {/* View mode selector — own row, fully scrollable */}
      <div className="shrink-0 border-b overflow-x-auto" style={{ WebkitOverflowScrolling: 'touch' }}>
        <div className="flex gap-1 px-2 py-1.5 min-w-max">
          {[
            { id: 'cards', icon: LayoutGrid, label: 'Cards' },
            { id: 'compact', icon: List, label: 'List' },
            { id: 'integrations', icon: Cable, label: 'Flow' },
            { id: 'simple-grid', icon: Grid3x3, label: 'Grid' },
            { id: 'swimlanes', icon: Columns3, label: 'Lanes' },
            { id: 'matrix', icon: Table2, label: 'Matrix' },
            { id: 'timeline', icon: Clock, label: 'Timeline' },
            { id: 'treemap', icon: DollarSign, label: 'Treemap' },
            { id: 'pulse', icon: Activity, label: 'Pulse' },
          ].map(v => (
            <button key={v.id} onClick={() => setViewMode(v.id)}
              className={`shrink-0 px-2.5 py-1.5 rounded-lg text-[10px] font-medium flex items-center gap-1.5 whitespace-nowrap transition-colors ${
                viewMode === v.id ? 'bg-primary text-primary-foreground shadow-sm' : 'bg-muted/50 text-muted-foreground'
              }`}>
              <v.icon className="h-3.5 w-3.5" /> {v.label}
            </button>
          ))}
        </div>
      </div>

      {/* (a) Swipeable category tabs */}
      <div className="shrink-0 border-b overflow-hidden">
        <div ref={catTabsRef} className="flex overflow-x-auto scrollbar-none gap-0.5 px-2 py-1.5"
          style={{ WebkitOverflowScrolling: 'touch' }}>
          {categories.map(([catName, cat]) => {
            const usedCount = cat.nodes.filter(n => n.used).length;
            const isActive = activeCatTab === catName;
            return (
              <button key={catName} onClick={() => {
                setActiveCatTab(isActive ? null : catName);
                if (!isActive) {
                  const el = document.getElementById(`cat-section-${catName.replace(/\s/g, '-')}`);
                  el?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                }
              }}
                className={`shrink-0 flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] font-medium transition-colors ${
                  isActive ? 'bg-foreground text-background' : 'bg-muted/50 text-muted-foreground hover:bg-muted'
                }`}>
                <div className="w-2 h-2 rounded-full shrink-0" style={{ backgroundColor: cat.color }} />
                {catName.split(' ')[0]}
                <span className="text-[8px] opacity-60">{usedCount}</span>
              </button>
            );
          })}
        </div>
      </div>

      {/* (d) Mini connection web — horizontal bar, collapsible */}
      {isPropertyView && viewMode === 'cards' && webNodes.length >= 3 && webNodes.length <= 20 && !hideWeb && (
        <div className="shrink-0 border-b px-2 py-1 flex items-center gap-1">
          <svg width="100%" height="50" viewBox="0 0 320 50" className="flex-1 opacity-80" preserveAspectRatio="xMidYMid meet">
            {/* Lines */}
            {webNodes.map((n, i) => {
              const x = 16 + (i / (webNodes.length - 1 || 1)) * 288;
              return getConnections(n.id)
                .filter(c => webNodes.some(wn => wn.id === c.id))
                .map((c, ci) => {
                  const j = webNodes.findIndex(wn => wn.id === c.id);
                  if (j <= i) return null;
                  const x2 = 16 + (j / (webNodes.length - 1 || 1)) * 288;
                  const curveY = 10 + Math.abs(j - i) * 3;
                  return (
                    <path key={`${i}-${ci}`} d={`M${x},25 Q${(x+x2)/2},${curveY} ${x2},25`}
                      fill="none" stroke={c.status === 'gap' ? '#f87171' : '#93c5fd'} strokeWidth="1" opacity="0.5" />
                  );
                });
            })}
            {/* Nodes */}
            {webNodes.map((n, i) => {
              const x = 16 + (i / (webNodes.length - 1 || 1)) * 288;
              const isSelected = selectedId === n.id;
              return (
                <g key={n.id} onClick={() => handleSelect(n)} style={{ cursor: 'pointer' }}>
                  <rect x={x-10} y={15} width="20" height="20" rx="4"
                    fill={n.category_color || '#6366f1'} stroke={isSelected ? '#000' : 'white'} strokeWidth={isSelected ? 2 : 1} />
                  <text x={x} y={46} textAnchor="middle" className="text-[5px] fill-muted-foreground">{(n.short || '').slice(0, 4)}</text>
                </g>
              );
            })}
          </svg>
          <button onClick={() => setHideWeb(true)} className="p-1 rounded hover:bg-muted/60 shrink-0" title="Hide web">
            <X className="h-3 w-3 text-muted-foreground" />
          </button>
        </div>
      )}
      {isPropertyView && viewMode === 'cards' && hideWeb && (
        <button onClick={() => setHideWeb(false)} className="shrink-0 border-b px-3 py-1 text-[9px] text-primary hover:bg-muted/40 text-center w-full">
          Show connection web
        </button>
      )}

      <ScrollArea className="flex-1">
        <div className="p-3 space-y-4">
          {/* (e) Integration flow list view */}
          {viewMode === 'integrations' && (
            <div className="space-y-1.5">
              <p className="text-[9px] font-semibold text-muted-foreground uppercase tracking-wider mb-2">Integration Connections ({integrationPairs.length})</p>
              {integrationPairs.length === 0 ? (
                <p className="text-xs text-muted-foreground text-center py-8">No integrations between active platforms.</p>
              ) : integrationPairs.map((pair, i) => (
                <button key={i} onClick={() => handleSelect(pair.source)}
                  className={`w-full text-left p-2.5 rounded-lg border transition-colors text-xs ${
                    pair.status === 'gap' ? 'border-red-200 bg-red-50/50' :
                    pair.confirmed ? 'border-emerald-200 bg-emerald-50/50' :
                    pair.level === 'product' ? 'border-violet-200 bg-violet-50/30' :
                    'border-blue-200 bg-blue-50/30'
                  } hover:shadow-sm`}
                  data-testid={`integ-pair-${i}`}>
                  <div className="flex items-center gap-2">
                    <div className="w-2 h-2 rounded-full shrink-0" style={{ backgroundColor: pair.source.category_color }} />
                    <span className="font-semibold">{pair.source.short || pair.source.name}{pair.source.product_name ? ` (${pair.source.product_name})` : ''}</span>
                    <span className="text-muted-foreground">{pair.direction === 'two-way' ? '↔' : '→'}</span>
                    <span className="font-semibold">{pair.target.short || pair.target.name}{pair.target.product_name ? ` (${pair.target.product_name})` : ''}</span>
                    <div className="flex-1" />
                    {pair.label && <span className="text-[9px] text-muted-foreground">{pair.label}</span>}
                    <Badge variant="outline" className={`text-[7px] ${
                      pair.status === 'gap' ? 'text-red-500 border-red-300' :
                      pair.confirmed ? 'text-emerald-600 border-emerald-300' : ''
                    }`}>{pair.status === 'gap' ? 'gap' : pair.confirmed ? 'live' : pair.level}</Badge>
                  </div>
                  {pair.level === 'product' && (pair.fromProduct || pair.toProduct) && (
                    <div className="mt-1 text-[9px] text-muted-foreground pl-4">
                      {pair.fromProduct} ↔ {pair.toProduct}
                    </div>
                  )}
                </button>
              ))}
            </div>
          )}

          {/* (c) Compact list view */}
          {viewMode === 'compact' && categories.map(([catName, cat]) => {
            const isExpanded = expandedCats[catName] ?? true;
            const usedCount = cat.nodes.filter(n => n.used).length;
            return (
              <div key={catName} id={`cat-section-${catName.replace(/\s/g, '-')}`}>
                <button onClick={() => setExpandedCats(p => ({ ...p, [catName]: !isExpanded }))}
                  className="flex items-center gap-2 mb-1 w-full text-left">
                  <div className="w-2.5 h-2.5 rounded-full shrink-0" style={{ backgroundColor: cat.color }} />
                  <span className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">{catName}</span>
                  <span className="text-[9px] text-muted-foreground">{usedCount}/{cat.nodes.length}</span>
                  <ChevronDown className={`h-2.5 w-2.5 text-muted-foreground ml-auto transition-transform ${!isExpanded ? '-rotate-90' : ''}`} />
                </button>
                {isExpanded && (
                  <div className="space-y-0.5 mb-3">
                    {cat.nodes.filter(n => !hideUnconnected || n.used).map(node => {
                      const conns = getConnections(node.id);
                      const isSelected = selectedId === node.id;
                      return (
                        <button key={node.id} onClick={() => handleSelect(node)}
                          className={`w-full flex items-center gap-2 px-2 py-1.5 rounded text-xs transition-colors ${
                            isSelected ? 'bg-primary/10 font-medium' : 'hover:bg-muted/40'
                          } ${!node.used ? 'opacity-40' : ''}`}>
                          {/* (b) Status dot */}
                          <div className={`w-2 h-2 rounded-full shrink-0 ${getStatusDot(node)}`} />
                          <span className="font-medium truncate w-16 text-left" style={{ color: cat.color }}>{node.short}</span>
                          <span className="flex-1 truncate text-left">{node.product_name || node.vendor_name || '—'}</span>
                          {conns.length > 0 && <span className="text-[8px] text-muted-foreground shrink-0">{conns.length}x</span>}
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}

          {/* Cards view (default) */}
          {viewMode === 'cards' && categories.map(([catName, cat]) => {
            const isExpanded = expandedCats[catName] ?? true;
            const usedCount = cat.nodes.filter(n => n.used).length;
            const selectedConns = selectedId ? getConnections(selectedId) : [];
            const connectedIds = new Set(selectedConns.map(c => c.id));

            return (
              <div key={catName} id={`cat-section-${catName.replace(/\s/g, '-')}`}>
                <button onClick={() => setExpandedCats(p => ({ ...p, [catName]: !isExpanded }))}
                  className="flex items-center gap-2 mb-2 w-full text-left group">
                  <span className="inline-flex items-center px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider text-white shadow-sm" style={{ backgroundColor: cat.color }}>
                    {catName}
                  </span>
                  <span className="text-[10px] text-muted-foreground ml-1">{usedCount}/{cat.nodes.length}</span>
                  <ChevronDown className={`h-3 w-3 text-muted-foreground ml-auto transition-transform ${!isExpanded ? '-rotate-90' : ''}`} />
                </button>
                {isExpanded && (
                  <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-2.5">
                    {cat.nodes.filter(n => !hideUnconnected || n.used).map(node => {
                      const isSelected = selectedId === node.id;
                      const isConnected = selectedId && connectedIds.has(node.id);
                      const isDimmed = selectedId && !isSelected && !isConnected;
                      const nodeConns = getConnections(node.id);
                      const isSwiped = swipedCard?.nodeId === node.id;

                      return (
                        <div key={node.id} className="relative"
                          onTouchStart={e => handleTouchStart(e, node)}
                          onTouchMove={handleTouchMove}
                          onTouchEnd={handleTouchEnd}>
                          {/* (g) Swipe reveal panels */}
                          {isSwiped && (
                            <div className={`absolute inset-0 z-10 rounded-lg flex items-center justify-center text-xs font-medium animate-in fade-in duration-150 ${
                              swipedCard.direction === 'left' ? 'bg-blue-50 text-blue-700' : 'bg-violet-50 text-violet-700'
                            }`}>
                              {swipedCard.direction === 'left' ? (
                                <div className="p-3 text-center">
                                  <Cable className="h-4 w-4 mx-auto mb-1" />
                                  <p className="font-bold mb-1">{nodeConns.length} connections</p>
                                  <div className="flex flex-wrap gap-1 justify-center">
                                    {nodeConns.slice(0, 4).map((c, i) => <span key={i} className="text-[8px] px-1.5 py-0.5 bg-white rounded">{c.direction === 'two-way' ? '↔' : '→'} {c.name}</span>)}
                                  </div>
                                </div>
                              ) : (
                                <div className="p-3 text-center">
                                  <Info className="h-4 w-4 mx-auto mb-1" />
                                  <p className="font-bold">{node.product_name || node.vendor_name || 'Not assigned'}</p>
                                  {node.vendor_company && <p className="text-[9px] opacity-70 mt-0.5">{node.vendor_company}</p>}
                                </div>
                              )}
                              <button onClick={() => setSwipedCard(null)} className="absolute top-1 right-1 p-1 rounded hover:bg-white/50"><X className="h-3 w-3" /></button>
                            </div>
                          )}

                          <button onClick={() => handleSelect(node)}
                            className={`w-full text-left rounded-xl overflow-hidden border shadow-sm transition-all duration-200 ${
                              isSelected ? 'ring-2 ring-offset-1 shadow-lg scale-[1.02]' :
                              isConnected ? 'ring-1 shadow-md' :
                              isDimmed ? 'opacity-25' :
                              'hover:shadow-md hover:-translate-y-0.5'
                            } ${node.used ? '' : 'opacity-40'}`}
                            style={{ borderColor: isSelected || isConnected ? cat.color : '#e2e8f0', '--tw-ring-color': cat.color }}
                            data-testid={`card-${node.id}`}>
                            {/* Header */}
                            <div className="px-3 py-1.5 flex items-center gap-1.5" style={{ backgroundColor: cat.color }}>
                              {/* (b) Status dot */}
                              <div className={`w-2 h-2 rounded-full ${getStatusDot(node)} ring-1 ring-white/30`} />
                              <span className="text-[10px] font-bold text-white uppercase tracking-wider flex-1">{node.short || node.name}</span>
                            </div>
                            <div className="px-3 py-1" style={{ backgroundColor: lighten(cat.color, 60) }}>
                              <span className="text-[9px] font-medium" style={{ color: cat.color }}>{node.name}</span>
                            </div>
                            <div className="px-3 py-2 bg-white min-h-[36px] flex items-center gap-2">
                              {node.used && vendorLogoUrl(node) && (
                                <img src={vendorLogoUrl(node)} alt="" loading="lazy"
                                  className="h-7 w-7 rounded-md border bg-white object-contain p-0.5 shrink-0"
                                  onError={e => { e.target.style.display = 'none'; }} />
                              )}
                              <div className="min-w-0 flex-1">
                                {node.used ? (
                                  <>
                                    <span className="text-xs font-bold text-[#1e293b] block leading-tight">{node.product_name || node.vendor_name || '—'}</span>
                                    {node.vendor_company && node.vendor_company !== node.product_name && (
                                      <span className="text-[8px] text-muted-foreground block mt-0.5">{node.vendor_company}</span>
                                    )}
                                  </>
                                ) : (
                                  <span className="text-[10px] text-muted-foreground/50 italic">Not assigned</span>
                                )}
                              </div>
                            </div>
                            {/* Integration connections */}
                            {node.used && nodeConns.length > 0 && (
                              <div className="px-2 py-1.5 bg-slate-50 border-t">
                                <div className="flex items-center gap-1 flex-wrap">
                                  {nodeConns.slice(0, 4).map((c, ci) => (
                                    <span key={ci} className={`text-[7px] px-1.5 py-0.5 rounded inline-flex items-center gap-0.5 font-medium ${
                                      c.status === 'gap' ? 'bg-red-50 text-red-500 line-through' :
                                      c.level === 'product' && c.confirmed ? 'bg-emerald-50 text-emerald-600' :
                                      c.level === 'product' ? 'bg-violet-50 text-violet-600' :
                                      'bg-blue-50 text-blue-600'
                                    }`}>
                                      {c.direction === 'two-way' ? '↔' : '→'} {c.name}{c.productName ? `: ${c.productName}` : ''}
                                    </span>
                                  ))}
                                  {nodeConns.length > 4 && <span className="text-[7px] text-muted-foreground">+{nodeConns.length - 4}</span>}
                                </div>
                              </div>
                            )}
                          </button>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </div>

          {/* New mobile views */}
          {viewMode === 'simple-grid' && (
            <SimpleGridView nodes={nodes} links={links} productLinks={productLinks} onSelect={handleSelect} selectedId={selectedId} />
          )}
          {viewMode === 'timeline' && (
            <TimelineView nodes={nodes} onSelect={handleSelect} />
          )}
          {viewMode === 'treemap' && (
            <SpendTreemap nodes={nodes} onSelect={handleSelect} />
          )}
          {viewMode === 'matrix' && (
            <IntegrationMatrix nodes={nodes} links={links} productLinks={productLinks} onSelect={handleSelect} />
          )}
          {viewMode === 'swimlanes' && (
            <SwimlanesView nodes={nodes} links={links} productLinks={productLinks} onSelect={handleSelect} selectedId={selectedId} />
          )}
          {viewMode === 'pulse' && (
            <HealthPulseView nodes={nodes} links={links} productLinks={productLinks} />
          )}
      </ScrollArea>

      {/* (f) Long press quick actions menu */}
      {longPressNode && (
        <div className="fixed inset-0 z-50" onClick={() => setLongPressNode(null)}>
          <div className="absolute bg-card border rounded-xl shadow-2xl p-1 w-44 animate-in fade-in zoom-in-95 duration-150"
            style={{ top: Math.min(longPressNode.y, window.innerHeight - 200), left: Math.min(longPressNode.x - 88, window.innerWidth - 180) }}>
            <p className="text-[10px] font-bold text-muted-foreground px-3 py-1.5 truncate">{longPressNode.node.short || longPressNode.node.name}</p>
            <button className="w-full flex items-center gap-2 px-3 py-2 text-xs hover:bg-muted/60 rounded-md" onClick={(e) => { e.stopPropagation(); handleSelect(longPressNode.node); setLongPressNode(null); }}>
              <Info className="h-3.5 w-3.5" /> View Details
            </button>
            <button className="w-full flex items-center gap-2 px-3 py-2 text-xs hover:bg-muted/60 rounded-md" onClick={(e) => { e.stopPropagation(); setSwipedCard({ nodeId: longPressNode.node.id, direction: 'left' }); setLongPressNode(null); }}>
              <Cable className="h-3.5 w-3.5" /> See Integrations
            </button>
            <button className="w-full flex items-center gap-2 px-3 py-2 text-xs hover:bg-muted/60 rounded-md" onClick={(e) => { e.stopPropagation(); setSwipedCard({ nodeId: longPressNode.node.id, direction: 'right' }); setLongPressNode(null); }}>
              <ArrowRightLeft className="h-3.5 w-3.5" /> Product Detail
            </button>
          </div>
        </div>
      )}

      {/* Selected node detail panel — compact, collapsible */}
      {selectedId && viewMode !== 'integrations' && (() => {
        const selNode = nodes.find(n => n.id === selectedId);
        const conns = getConnections(selectedId);
        const platformConns = conns.filter(c => c.level === 'platform');
        const productConns = conns.filter(c => c.level === 'product');
        return (
          <div className="shrink-0 border-t bg-card/90 backdrop-blur mb-mobile-nav">
            {/* Compact header — always visible */}
            <div className="flex items-center gap-2 px-3 py-1.5">
              <div className="w-2 h-2 rounded-full shrink-0" style={{ backgroundColor: selNode?.category_color }} />
              <span className="text-xs font-bold flex-1">{selNode?.short || selectedId}</span>
              <span className="text-[9px] text-muted-foreground">{conns.length} connections</span>
              <button onClick={() => setSelectedId(null)} className="text-[10px] text-muted-foreground hover:text-foreground px-1">✕</button>
            </div>
            {/* Connection pills — single scrollable row */}
            {conns.length > 0 && (
              <div className="px-3 pb-2 flex gap-1 overflow-x-auto" style={{ WebkitOverflowScrolling: 'touch' }}>
                {[...platformConns, ...productConns].map((c, i) => (
                  <span key={i} className={`shrink-0 text-[8px] px-2 py-0.5 rounded-full border inline-flex items-center gap-0.5 ${
                    c.status === 'gap' ? 'bg-red-50 border-red-200 text-red-600' :
                    c.level === 'product' && c.confirmed ? 'bg-emerald-50 border-emerald-200 text-emerald-700' :
                    c.level === 'product' ? 'bg-violet-50 border-violet-200 text-violet-600' : 'bg-card'}`}>
                    {c.direction === 'two-way' ? '↔' : '→'} {c.name}{c.productName ? ` (${c.productName})` : ''}{c.label ? ` · ${c.label}` : ''}
                  </span>
                ))}
              </div>
            )}
          </div>
        );
      })()}
    </div>
  );
}
